import {
  isRef,
  onMounted,
  onUnmounted,
  watchEffect,
  type ComputedRef,
  type Ref,
} from 'vue'
import {
  formFloatingActions,
  hideFormFloatingActions,
  registerFormFloatingActions,
  type FormFloatingActionsConfig,
} from '@/utils/formFloatingActions'

type FormErrorsMap = Record<string, string>

export interface UseFormFloatingActionsOptions {
  validate: () => boolean
  submit: () => void | Promise<void>
  cancel?: () => void
  getSubmitLabel?: () => string
  getLoading?: () => boolean
  formErrors?: FormErrorsMap | Ref<FormErrorsMap>

  active?: Ref<boolean> | ComputedRef<boolean> | (() => boolean)
}

export function runSilentValidate(
  formErrors: FormErrorsMap | Ref<FormErrorsMap> | undefined,
  validate: () => boolean,
): boolean {
  if (!formErrors) return validate()

  if (isRef(formErrors)) {
    const snapshot = { ...formErrors.value }
    const valid = validate()
    formErrors.value = { ...snapshot }
    return valid
  }

  const snapshot = { ...formErrors }
  const valid = validate()
  for (const key of Object.keys(formErrors)) {
    if (!(key in snapshot)) {
      delete formErrors[key]
    }
  }
  Object.assign(formErrors, snapshot)
  return valid
}

export function useFormFloatingActions(options: UseFormFloatingActionsOptions): void {
  let stopWatch: (() => void) | undefined

  onMounted(() => {
    stopWatch = watchEffect(() => {
      const active =
        options.active === undefined
          ? true
          : typeof options.active === 'function'
            ? options.active()
            : options.active.value

      if (!active) {
        hideFormFloatingActions()
        return
      }

      const config: FormFloatingActionsConfig = {
        submit: options.submit,
        cancel: options.cancel,
        getSubmitLabel: options.getSubmitLabel,
        getLoading: options.getLoading,
        validate: options.validate,
        getCanSubmit: () => runSilentValidate(options.formErrors, options.validate),
      }

      const canSubmit = runSilentValidate(options.formErrors, options.validate)

      registerFormFloatingActions(config)
      formFloatingActions.canSubmit = canSubmit
    })
  })

  onUnmounted(() => {
    stopWatch?.()
    hideFormFloatingActions()
  })
}
